import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RemoveMemberModal = ({ isOpen, onClose, member, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (confirmText.toLowerCase() === 'remove') {
      onConfirm(member, reason);
    }
  };

  const handleClose = () => {
    setReason('');
    setConfirmText('');
    onClose();
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-spiritual-md w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-full flex items-center justify-center">
              <Icon name="UserX" size={20} className="text-error" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">
                Remove Member
              </h2>
              <p className="text-sm text-muted-foreground">
                This action cannot be undone
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Member Info */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={16} className="text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-medium text-foreground">
                  {member.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {member.district} • {member.samithi}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Mobile:</span>
                <p className="font-mono">{member.mobile}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Age:</span>
                <p>{member.age} years</p>
              </div>
            </div>
          </div>

          {/* Reason Input */}
          <Input
            label="Reason for Removal"
            type="text"
            placeholder="Enter reason for removing this member..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            description="Please provide a reason for removing this member from the system"
          />

          {/* Confirmation Input */}
          <Input
            label="Confirmation"
            type="text"
            placeholder="Type 'REMOVE' to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            required
            description="Type 'REMOVE' in capital letters to confirm this action"
          />

          {/* Warning Message */}
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-heading font-medium text-error mb-1">
                  Warning: Permanent Removal
                </h4>
                <p className="text-sm text-error/80">
                  This member will be permanently removed from the system. All their registration data and service history will be deleted. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              fullWidth
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="destructive"
              loading={loading}
              disabled={!reason.trim() || confirmText.toLowerCase() !== 'remove'}
              iconName="Trash2"
              iconPosition="left"
              fullWidth
            >
              Remove Member
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RemoveMemberModal;